/**
 * Anti-debugging and anti-tampering utilities
 * Makes it extremely difficult to debug or inspect the application
 */

let devtoolsOpen = false;
const threshold = 200;

const isMobileDevice = () => {
  const mobileUA = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIPadOS = navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent);
  return mobileUA || isIPadOS;
};

const isSafariBrowser = () => {
  const ua = navigator.userAgent;
  return ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
};

const handleDevToolsOpen = () => {
  console.clear();
  document.body.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:Arial;"><div><h1>⚠️ Developer Tools Detected</h1><p>For security reasons, this application cannot run with developer tools open.</p></div></div>';
};

// Size-based detection — only reliable on desktop Chrome/Firefox/Edge.
// Mobile browser chrome and Safari sidebars cause false positives.
const detectDevTools = () => {
  if (isMobileDevice() || isSafariBrowser()) return;

  const widthThreshold = window.outerWidth - window.innerWidth > threshold;
  const heightThreshold = window.outerHeight - window.innerHeight > threshold;
  
  if (widthThreshold || heightThreshold) {
    if (!devtoolsOpen) {
      devtoolsOpen = true;
      handleDevToolsOpen();
    }
  } else {
    devtoolsOpen = false;
  }
};

// Console-based detection — getter fires only when DevTools renders the logged object.
// Safe to run on desktop Safari but skipped on mobile where console.log is a no-op.
const detectDevToolsAdvanced = () => {
  if (isMobileDevice()) return;

  const element = new Image();
  Object.defineProperty(element, 'id', {
    get: function() {
      devtoolsOpen = true;
      handleDevToolsOpen();
    }
  });
  console.log(element);
  console.clear();
};

// Debugger-timing detection — only meaningful on desktop
const antiDebugger = () => {
  if (isMobileDevice()) return;

  setInterval(() => {
    const start = performance.now();
    debugger;
    const end = performance.now();
    if (end - start > 100) {
      handleDevToolsOpen();
    }
  }, 1000);
};

// Disable console methods
export const disableConsole = () => {
  const noop = () => {};
  const methods = ['log', 'debug', 'info', 'warn', 'error', 'dir', 'trace', 'assert', 'clear'];
  
  methods.forEach(method => {
    if (console[method]) {
      console[method] = noop;
    }
  });
};

// Prevent right-click
export const disableRightClick = () => {
  document.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    return false;
  });
};

// Prevent common keyboard shortcuts
export const disableKeyboardShortcuts = () => {
  document.addEventListener('keydown', (e) => {
    // F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Ctrl+U
    if (
      e.keyCode === 123 || // F12
      (e.ctrlKey && e.shiftKey && e.keyCode === 73) || // Ctrl+Shift+I
      (e.ctrlKey && e.shiftKey && e.keyCode === 74) || // Ctrl+Shift+J
      (e.ctrlKey && e.shiftKey && e.keyCode === 67) || // Ctrl+Shift+C
      (e.ctrlKey && e.keyCode === 85) || // Ctrl+U
      (e.ctrlKey && e.keyCode === 83) // Ctrl+S
    ) {
      e.preventDefault();
      return false;
    }
  });
};

// Detect if code has been tampered with
let integrityChecksum = null;

export const calculateIntegrity = () => {
  const scripts = Array.from(document.scripts)
    .filter(s => s.src.includes('main'))
    .map(s => s.src)
    .join('');
  
  // Simple checksum
  let hash = 0;
  for (let i = 0; i < scripts.length; i++) {
    const char = scripts.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return hash;
};

export const verifyIntegrity = () => {
  if (integrityChecksum === null) {
    integrityChecksum = calculateIntegrity();
    return true;
  }
  
  const currentChecksum = calculateIntegrity();
  if (currentChecksum !== integrityChecksum) {
    // Code has been modified
    alert('Security violation detected. Application will reload.');
    window.location.reload();
    return false;
  }
  
  return true;
};

// Detect iframe embedding (prevent clickjacking)
export const preventIframeEmbedding = () => {
  if (window.self !== window.top) {
    window.top.location = window.self.location;
  }
};

// Clear local storage on suspicious activity
export const clearStorageOnViolation = () => {
  localStorage.clear();
  sessionStorage.clear();
};

// Obfuscate console output
export const obfuscateConsole = () => {
  const originalLog = console.log;
  console.log = function(...args) {
    const obfuscated = args.map(arg => {
      if (typeof arg === 'string') {
        return btoa(arg);
      }
      return arg;
    });
    originalLog.apply(console, obfuscated);
  };
};

// Infinite loop to slow down debugging
const createDebuggerLoop = () => {
  (function() {
    function detectDebug(a) {
      if (('' + a / a)['length'] !== 1 || a % 20 === 0) {
        (function() {}).constructor('debugger')();
      } else {
        (function() {}).constructor('debugger')();
      }
      detectDebug(++a);
    }
    try {
      detectDebug(0);
    } catch (err) {}
  })();
};

// Initialize all protections
export const initializeProtection = (options = {}) => {
  const {
    disableDevTools = true,
    disableRightClickMenu = true,
    disableKeyboardShortcuts: disableKbShortcuts = true,
    preventIframe = true,
    checkIntegrity = true,
    antiDebug = false // Very aggressive, may cause performance issues
  } = options;

  if (process.env.NODE_ENV === 'production') {
    if (disableDevTools) {
      setInterval(detectDevTools, 500);
      setTimeout(detectDevToolsAdvanced, 1000);
    }
    
    if (disableRightClickMenu) {
      disableRightClick();
    }
    
    if (disableKbShortcuts) {
      disableKeyboardShortcuts();
    }
    
    if (preventIframe) {
      preventIframeEmbedding();
    }
    
    if (checkIntegrity) {
      setInterval(verifyIntegrity, 5000);
    }
    
    if (antiDebug) {
      antiDebugger();
      // createDebuggerLoop(); // VERY aggressive, uncomment if needed
    }
  }
};

// Check if running in production
export const isProduction = () => {
  return process.env.NODE_ENV === 'production' && 
         !window.location.hostname.includes('localhost');
};

// Encode sensitive data
export const encode = (str) => {
  return btoa(encodeURIComponent(str));
};

export const decode = (str) => {
  return decodeURIComponent(atob(str));
};

// Random string obfuscation
const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
export const obfuscate = (str) => {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    const randomChar = chars[Math.floor(Math.random() * chars.length)];
    result += randomChar + String.fromCharCode(char ^ 0x5A);
  }
  return btoa(result);
};

export const deobfuscate = (str) => {
  const decoded = atob(str);
  let result = '';
  for (let i = 1; i < decoded.length; i += 2) {
    const char = decoded.charCodeAt(i);
    result += String.fromCharCode(char ^ 0x5A);
  }
  return result;
};

// Prevent copy-paste
export const preventCopyPaste = () => {
  document.addEventListener('copy', (e) => {
    e.preventDefault();
    e.clipboardData.setData('text/plain', '');
  });
  
  document.addEventListener('cut', (e) => {
    e.preventDefault();
  });
};

// Monitor for network debugging tools
export const detectNetworkMonitoring = () => {
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    // If someone is intercepting fetch, we'll know
    console.warn('Network monitoring detected');
    return originalFetch.apply(this, args);
  };
};

export default {
  initializeProtection,
  disableConsole,
  disableRightClick,
  disableKeyboardShortcuts,
  verifyIntegrity,
  preventIframeEmbedding,
  isProduction,
  encode,
  decode,
  obfuscate,
  deobfuscate,
  preventCopyPaste,
  detectNetworkMonitoring
};

